// src/features/records/hooks/useRecordsLayoutContext.js
import { useOutletContext } from 'react-router-dom';

/** @type {() => void} */
const noop = () => {};

const DEFAULT_CONTEXT = Object.freeze({
  searchQuery: '',
  setSearchQuery: noop,
  onLinkToRecord: noop,
  onEditRecord: noop
});

let hasWarnedMissingContext = false;

/**
 * @typedef {Object} RecordsLayoutContext
 * @property {string} searchQuery - Current search query string
 * @property {(query: string) => void} setSearchQuery - Update search query
 * @property {(recordId: string) => void} onLinkToRecord - Handle record link click
 * @property {(record: any) => void} onEditRecord - Handle record edit
 */

/**
 * Hook to access RecordsLayout outlet context
 * Provides type-safe access to search state and layout callbacks
 *
 * @returns {RecordsLayoutContext}
 */
export function useRecordsLayoutContext() {
  const context = /** @type {RecordsLayoutContext | undefined} */ (useOutletContext());

  if (!context) {
    if (process.env.NODE_ENV !== 'production' && !hasWarnedMissingContext) {
      console.warn('useRecordsLayoutContext: missing outlet context; using safe defaults.');
      hasWarnedMissingContext = true;
    }
    return DEFAULT_CONTEXT;
  }

  return context;
}
